import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { prebuilt, type PrebuiltCampaign, type PrebuiltStep } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ArrowLeft, Mail, Calendar, Clock, Target, Download,
  ChevronDown, ChevronUp, Check, Loader2, Zap, Send,
  Eye, ShoppingCart, Monitor, Home, Wrench, GraduationCap,
  Building2, HeartPulse, Globe, Sparkles, Copy, ExternalLink,
} from "lucide-react";

// ── Industry icons ──
const INDUSTRY_MAP: Record<string, { label: string; icon: any; color: string }> = {
  ecommerce:      { label: "E-commerce",     icon: ShoppingCart,  color: "bg-coral-light text-coral" },
  saas:           { label: "SaaS",           icon: Monitor,       color: "bg-blue-50 text-blue-600" },
  real_estate:    { label: "Real Estate",    icon: Home,          color: "bg-emerald-50 text-emerald-600" },
  local_services: { label: "Local Services", icon: Wrench,        color: "bg-amber-50 text-amber-700" },
  education:      { label: "Education",      icon: GraduationCap, color: "bg-purple-50 text-purple-600" },
  agency:         { label: "Agency / B2B",   icon: Building2,     color: "bg-indigo-50 text-indigo-600" },
  health:         { label: "Health",         icon: HeartPulse,    color: "bg-rose-50 text-rose-600" },
  general:        { label: "Universal",      icon: Sparkles,      color: "bg-teal-light text-teal" },
};

const DIFFICULTY_BADGE: Record<string, string> = {
  beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
  intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  advanced: "bg-rose-50 text-rose-700 border-rose-200",
};

const EMAIL_TYPE_EMOJI: Record<string, string> = {
  welcome: "👋", announcement: "📢", teaser: "👀", social_proof: "⭐",
  incentive: "🎁", last_call: "🚨", urgency: "⏰", reminder: "🔔",
  brand_story: "📖", thank_you: "🙏", education: "💡", cross_sell: "🛍️",
  review_request: "📝", re_engage: "💛", check_in: "💬", quick_win: "⚡",
  feature_highlight: "✨", case_study: "📈", upgrade_nudge: "🔑",
  trial_ending: "⏳", value_proposition: "💰", limited_offer: "🏷️",
  neighborhood: "🏘️", virtual_tour: "🎥", open_house: "🏡",
  market_update: "📊", consultation: "🤝", featured_listings: "🏠",
  seasonal_reminder: "🌿", early_bird: "🐦", referral: "💌",
  delivery: "📦", deep_dive: "🔍", story: "📕", solution: "🎯",
  curriculum: "📋", testimonials: "💬", bonuses_faq: "🎁",
  cart_close: "🔒", value_first: "💎", soft_cta: "🤲", breakup: "👋",
  team_intro: "👥", milestone: "🏁", review: "📊",
  preparation: "📋", follow_up: "💬",
  kickoff: "🏆", daily_tip: "📅", motivation: "💪", completion: "🎉",
  best_of: "🏅", final_goodbye: "👋",
  personal_outreach: "🤝", value_reminder: "💡",
  pitch: "🎤",
};

export default function PrebuiltDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<PrebuiltCampaign | null>(null);
  const [steps, setSteps] = useState<PrebuiltStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Install state
  const [showInstall, setShowInstall] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installedId, setInstalledId] = useState<string | null>(null);

  // Step expansion
  const [expandedStep, setExpandedStep] = useState<number | null>(0);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    prebuilt.get(slug)
      .then((res) => {
        setCampaign(res.campaign);
        setSteps(res.steps);
      })
      .catch((e) => setError(e.message || "Campaign not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  async function handleInstall() {
    if (!slug) return;
    setInstalling(true);
    try {
      const res = await prebuilt.install(slug, {
        brandName: brandName.trim() || undefined,
        ctaUrl: ctaUrl.trim() || undefined,
      });
      setInstalled(true);
      setInstalledId(res.sequence.id);
    } catch (e: any) {
      setError(e.message || "Failed to install campaign");
    } finally {
      setInstalling(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-red-500 mb-4">{error || "Campaign not found"}</p>
        <button onClick={() => navigate("/campaigns/prebuilt")} className="text-coral text-sm hover:underline">
          ← Back to Library
        </button>
      </div>
    );
  }

  const ind = INDUSTRY_MAP[campaign.industry] || INDUSTRY_MAP.general;
  const IndIcon = ind.icon;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back link */}
      <button
        onClick={() => navigate("/campaigns/prebuilt")}
        className="flex items-center gap-1.5 text-sm text-slate hover:text-ink transition-colors mb-6"
      >
        <ArrowLeft size={15} /> Back to Library
      </button>

      {/* ── Hero Card ── */}
      <div className="bg-white rounded-xl border border-sand p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${ind.color}`}>
                <IndIcon size={12} /> {ind.label}
              </span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${DIFFICULTY_BADGE[campaign.difficulty] || ""}`}>
                {campaign.difficulty}
              </span>
              {campaign.install_count > 0 && (
                <span className="text-[10px] text-slate/60 flex items-center gap-1">
                  <Download size={10} /> {campaign.install_count} installed
                </span>
              )}
            </div>

            <h1 className="font-display font-bold text-ink text-2xl leading-tight">
              {campaign.name}
            </h1>
            <p className="text-slate text-sm mt-2 leading-relaxed max-w-xl">
              {campaign.description}
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-5 mt-4">
              <span className="flex items-center gap-1.5 text-sm text-slate">
                <Mail size={14} className="text-coral" /> {campaign.step_count} emails
              </span>
              <span className="flex items-center gap-1.5 text-sm text-slate">
                <Calendar size={14} className="text-coral" /> {campaign.duration_days} day{campaign.duration_days !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Tags */}
            {campaign.tags && campaign.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {campaign.tags.map(tag => (
                  <span key={tag} className="text-[10px] text-slate/70 bg-paper px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Install CTA */}
          <div className="shrink-0">
            {installed ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-light rounded-full flex items-center justify-center mx-auto mb-2">
                  <Check size={22} className="text-teal" />
                </div>
                <p className="text-xs text-teal font-semibold">Installed!</p>
                <button
                  onClick={() => navigate("/campaigns")}
                  className="text-xs text-coral hover:underline mt-1"
                >
                  View Campaigns →
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowInstall(true)}
                className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                <Download size={15} /> Use This Campaign
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Install Modal ── */}
      {showInstall && !installed && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowInstall(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-lg text-ink mb-1">
              Install "{campaign.name}"
            </h2>
            <p className="text-xs text-slate mb-5">
              This will create {campaign.step_count} draft emails in your account. You can edit everything before sending.
            </p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-ink mb-1">Brand Name (optional)</label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Your company name"
                  className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral"
                />
                <p className="text-[10px] text-slate mt-0.5">Appears in email header and footer</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink mb-1">CTA Link URL (optional)</label>
                <input
                  type="url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://yoursite.com/shop"
                  className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral"
                />
                <p className="text-[10px] text-slate mt-0.5">Where CTA buttons link to</p>
              </div>
            </div>

            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={() => setShowInstall(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInstall}
                disabled={installing}
                className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {installing ? <><Loader2 size={14} className="animate-spin" /> Installing...</> : <><Download size={14} /> Install</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Email Timeline ── */}
      <div className="mb-4">
        <h2 className="font-display font-bold text-ink text-lg flex items-center gap-2">
          <Mail size={18} className="text-coral" /> Email Sequence
        </h2>
        <p className="text-xs text-slate mt-0.5">Click each email to preview the full content.</p>
      </div>

      <div className="space-y-3">
        {steps.map((step, i) => {
          const isExpanded = expandedStep === i;
          const emoji = EMAIL_TYPE_EMOJI[step.email_type] || "📧";
          return (
            <div
              key={step.id}
              className={`bg-white rounded-xl border transition-all ${
                isExpanded ? "border-coral/40 shadow-sm" : "border-sand hover:border-coral/20"
              }`}
            >
              {/* Step header */}
              <button
                onClick={() => setExpandedStep(isExpanded ? null : i)}
                className="w-full flex items-center gap-3 p-4 text-left"
              >
                {/* Day circle */}
                <div className="shrink-0 w-10 h-10 bg-paper rounded-full flex flex-col items-center justify-center">
                  <span className="text-[9px] uppercase text-slate font-semibold leading-none">Day</span>
                  <span className="text-sm font-bold text-ink leading-none">{step.day}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs">{emoji}</span>
                    <span className="text-[10px] font-semibold text-slate uppercase tracking-wide">
                      {step.email_type.replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-slate/50 ml-auto flex items-center gap-0.5">
                      <Clock size={9} /> {step.send_time}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-ink truncate">{step.subject}</p>
                  {step.goal && (
                    <p className="text-[11px] text-slate mt-0.5 flex items-center gap-1">
                      <Target size={10} /> {step.goal}
                    </p>
                  )}
                </div>

                {/* Expand chevron */}
                <div className="shrink-0">
                  {isExpanded ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
                </div>
              </button>

              {/* Expanded email preview */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0">
                  <div className="bg-paper rounded-lg p-5 border border-sand/60">
                    {/* Email header simulation */}
                    <div className="mb-4 pb-3 border-b border-sand/80">
                      <p className="text-xs text-slate mb-1">
                        <span className="font-semibold">Subject:</span> {step.subject}
                      </p>
                      {step.preview_text && (
                        <p className="text-xs text-slate/70">
                          <span className="font-semibold">Preview:</span> {step.preview_text}
                        </p>
                      )}
                    </div>

                    {/* Email body */}
                    <h3 className="font-display font-bold text-ink text-lg mb-3">
                      {step.body_heading}
                    </h3>
                    <div className="text-sm text-slate leading-relaxed space-y-3">
                      {step.body_text.split("\n\n").map((para, pi) => (
                        <p key={pi}>{para}</p>
                      ))}
                    </div>

                    {/* CTA button preview */}
                    <div className="mt-5 text-center">
                      <span className="inline-block bg-coral text-white px-6 py-2.5 rounded-md text-sm font-semibold">
                        {step.call_to_action}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      {!installed && (
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowInstall(true)}
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-8 py-3 rounded-lg text-sm font-semibold transition-colors shadow-sm"
          >
            <Download size={16} /> Use This Campaign — {campaign.step_count} Emails Ready to Go
          </button>
          <p className="text-xs text-slate mt-2">
            Creates draft emails you can fully customize before sending.
          </p>
        </div>
      )}

      {installed && (
        <div className="mt-8 bg-teal-light rounded-xl p-6 text-center">
          <Check size={32} className="mx-auto text-teal mb-2" />
          <h3 className="font-display font-bold text-ink text-lg">Campaign Installed!</h3>
          <p className="text-sm text-slate mt-1 mb-4">
            {campaign.step_count} draft emails have been added to your account. Customize the content, set your audience, and send when ready.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => navigate("/campaigns")}
              className="flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-coral-dark transition-colors"
            >
              <Send size={14} /> Go to Campaigns
            </button>
            <button
              onClick={() => navigate("/campaigns/prebuilt")}
              className="flex items-center gap-2 border border-sand px-5 py-2.5 rounded-lg text-sm font-medium text-slate hover:bg-paper transition-colors"
            >
              <ArrowLeft size={14} /> Browse More
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
